import {createApp} from "vue3";
import Konva from '../plugins/konva.min';
import '../plugins/gifler.min';

import QuizMap from "./quiz-map";
import QuizDialogue from "./quiz-dialogue";
import QuizDialogueCode from "./quiz-dialogue-code";
import QuizCode from "./quiz-code";
import QuizEnding from "./quiz-ending";

export default class Quiz {
    /**
     * Constructor
     * @param $el
     */
    constructor($el) {
        this.$el = $el;
        this.youtubeHasLoaded = false;

        window.onYouTubePlayerAPIReady = () => {
            this.youtubeHasLoaded = true;
        };

        __cmp("addEventListener", ["consent", this.init(), false], null);
    }

    /**
     * Init
     * @return {{scores: *, questions: (*|*[]), currentQuestion: null, currentAnswer: null}}
     */
    init() {
        const questions     = quizData.questions;
        const extra         = extraQuizData;
        const youtubeLoaded = this.youtubeHasLoaded;

        const app = createApp({
            data() {
                return {
                    questions: questions,
                    extraQuizData: extra,
                    code: extra.code,
                    referralHref: extra.referralHref,
                    referralLabel: extra.referralLabel,
                    codeArray: [],
                    codeReady: false,

                    activeQuestion: null,
                    completedQuestions: [],
                    codeVideoWasPlayed: false,

                    openingVideo: quizOpeningVideo,
                    showOpeningVideo: false,
                    openingVideoPlayer: null,
                    destroyOpeningVideo: false,
                    showcmpoverlay: false,
                    youtubeApiLoaded: youtubeLoaded,

                    quizEnded: false,
                    dialogueCodeAudioPlayed: false
                };
            },
            computed: {
                /**
                 * Check if the question we clicked is locked
                 * @returns {boolean|*}
                 */
                activeQuestionIsLocked() {
                    if (! this.activeQuestion) {
                        return false;
                    }

                    const extraQuestionData = this.extraQuizData.questions[this.activeQuestion.id];

                    return extraQuestionData.locked;
                },

                /**
                 * Check if a question was completed
                 * @returns {boolean|*}
                 */
                activeQuestionIsCompleted() {
                    if (! this.activeQuestion) {
                        return false;
                    }

                    return this.completedQuestions[this.activeQuestion.id];
                },

                noConsentClass() {
                    return this.showcmpoverlay ? 'quiz-video--no-consent' : '';
                }
            },
            methods: {
                /**
                 * When the code video was fully played we don't want to show it every time the question is opened again
                 */
                codeVideoPlayed() {
                    this.codeVideoWasPlayed = true;
                },

                /**
                 * Set the active question
                 * @param questionId
                 */
                startDialogue(questionId) {
                    this.activeQuestion = this.questions.find(question => question.id === questionId);
                },

                /**
                 * When a question was answered correctly
                 */
                completeQuestion() {
                    this.$refs.map.completeQuestionDot(this.activeQuestion.id);
                    this.completedQuestions[this.activeQuestion.id] = true;
                    // const codeEntry = this.codeArray.find(x => x.letter === this.extraQuizData.questions[this.activeQuestion.id].letter && ! x.complete);
                    // if (codeEntry) {
                    //     codeEntry.complete = true;
                    // }

                    this.activeQuestion = null;
                },

                /**
                 * Create an array that tracks our progress on unlocking the code
                 */
                createCode() {
                    const letterArray = Array.from(this.code);
                    letterArray.forEach(letter => {
                        this.codeArray.push({
                            letter: letter,
                            complete: true,
                            guess: letter,
                            correct: true
                        });
                    });

                    Object.keys(this.extraQuizData.questions).forEach(questionId => {
                        const entry = this.codeArray.find(x => x.letter === this.extraQuizData.questions[questionId].letter && x.complete);
                        if (entry) {
                            entry.complete = false;
                            entry.guess = null;
                            entry.correct = null;
                        }
                    });

                    this.codeReady = true;
                },

                /**
                 * Check if the guessed letter is correct and update the code array
                 * @param data
                 */
                updateCodeByGuess(data) {
                    if (! data.correct) {
                        this.codeArray[data.index].guess = data.guess;
                        this.codeArray[data.index].correct = data.correct;
                        return;
                    }

                    this.codeArray[data.index].complete = true;
                },

                /**
                 * When the code is completed
                 */
                codeCompleted() {
                    this.$refs.map.activateLockedQuestions();
                },

                unsetActiveQuestion() {
                    this.activeQuestion = null;
                },

                createOpeningVideoPlayer() {
                    this.showcmpoverlay = false;

                    // create a youtube player
                    this.openingVideoPlayer = new YT.Player(this.$refs.openingVideoPlayer, {
                        height: '360',
                        width: '640',
                        videoId: this.openingVideo,
                        playerVars: {
                            // controls: 0,
                        }
                    });
                },

                endQuiz() {
                    this.quizEnded = true;
                },

                closeOpeningVideo() {
                    this.$refs.openingVideoModal.addEventListener('animationend', () => {
                        if (this.openingVideoPlayer) {
                            this.openingVideoPlayer.destroy();
                        }
                        this.destroyOpeningVideo = true;
                    });

                    this.$refs.openingVideoModal.classList.add('quiz-dialogue-question--out');
                },

                startGame() {
                    if ( ! __cmp('getCMPData').vendorConsents['s30']) {
                        this.showcmpoverlay = true;
                    }

                    this.showOpeningVideo = true;
                },

                /**
                 * Give consent for youtube video's
                 */
                giveYoutubeConsent() {
                    __cmp('setVendorConsent', ['s30', 1 ]);
                    this.youtubeApiLoaded = true;
                },

                setDialogueCodeAudioPlayed() {
                    this.dialogueCodeAudioPlayed = true;
                }
            },
            mounted() {
                if (this.youtubeApiLoaded) {
                    this.createOpeningVideoPlayer();
                } else {
                    window.onYouTubePlayerAPIReady = () => {
                        this.youtubeApiLoaded = true;
                        this.createOpeningVideoPlayer();
                    };
                }

                this.createCode();
            }
        });

        app.config.compilerOptions.delimiters = ['v{', '}'];
        app.component('quiz-map', QuizMap);
        app.component('quiz-dialogue', QuizDialogue);
        app.component('quiz-dialogue-code', QuizDialogueCode);
        app.component('quiz-code', QuizCode);
        app.component('quiz-ending', QuizEnding);

        // mount the app
        app.mount(this.$el);
    }
}